<template>
    <o-project-wrapper
        :projectUuid="projectUuid"
        :onGetProjects="onGetProjects"
    >
        <div class="t-resource main">
            <h2>
                {{ $t('app.Resources') }}
            </h2>

            <div v-if="isReady && projectLanguages.length > 0">
                <div class="resource-search">
                    <span class="app-label">
                        {{ $t('app.Search') }}
                    </span>
                    <Input
                        search
                        v-model="searchQuery"
                        @on-search="searchResource"
                    />
                </div>

                <o-resource-list
                    ref="resourceList"
                    :resources="resources"
                    :onGetResources="getResources"
                    :onSearchGoogleDocs="onSearchGoogleDocs"
                    :onSetGoogleDocPermissions="onSetGoogleDocPermissions"
                    :languages="languages"
                    :projectInfo="project"
                    @addResource="showAddResourceDlg"
                    @deleteResource="showDeleteResourceDlg"
                    @editResource="showEditResourceDlg"
                    @updateGoogleDoc="updateGoogleDoc"
                    @translationGoogle="translationGoogle"
                    :tags="tags"
                />
            </div>
            <div v-else-if="isReady && projectLanguages.length == 0">
                {{ $t('app.ShouldAddLanguages') }}
            </div>
            <b-spinner v-else />

            <o-add-resource
                ref="addResource"
                :onAddResource="addResource"
                :onAddGoogleDoc="addGoogleDoc"
                :languages="languages"
                :onGetDefaultLangCode="getDefaultLangCode"
                :tags="tags"
                :onUploadFile="onUploadFile"
            />

            <o-edit-resource
                ref="editResource"
                :onEditResource="editResource"
                :onAddGoogleDoc="addGoogleDoc"
                :languages="languages"
                :tags="tags"
                :onUploadFile="onUploadFile"
                :onGetResourceByKey="onGetResourceByKey"
            />

            <o-delete-resource
                ref="deleteResource"
                :onDeleteResource="deleteResource"
            />
        </div>
    </o-project-wrapper>
</template>

<script>
import OProjectWrapper from '@organisms2/o-project-wrapper'
import OResourceList from '@organisms2/o-resource-list'
import OAddResource from '@organisms2/o-add-resource'
import OEditResource from '@organisms2/o-edit-resource'
import ODeleteResource from '@organisms2/o-delete-resource'
import { Input } from 'view-design'
import _ from 'lodash'

export default {
    name: 'TResource',
    components: {
        OProjectWrapper,
        OResourceList,
        Input,
        OAddResource,
        OEditResource,
        ODeleteResource
    },
    props: {
        projectUuid: {
            type: String,
            required: true,
        },
        onGetProjects: {
            type: Function,
            required: true,
        },
        onGetLanguages: {
            type: Function,
            required: true,
        },
        onGetProjectLanguages: {
            type: Function,
            required: true,
        },
        onGetResources: {
            type: Function,
            required: true,
        },
        onGetResourceByKey: {
            type: Function,
            required: true,
        },
        onAddResource: {
            type: Function,
            required: true,
        },
        onAddGoogleDoc: {
            type: Function,
            required: true,
        },
        onTranslationGoogle: {
            type: Function,
            required: true,
        },
        onEditResource: {
            type: Function,
            required: true,
        },
        onDeleteResource: {
            type: Function,
            required: true,
        },
        onGetTags: {
            type: Function,
            required: true,
        },
        onUploadFile: {
            type: Function,
            required: true
        },
        onSearchGoogleDocs: {
            type: Function,
            required: true,
        },
        onSetGoogleDocPermissions: {
            type: Function,
            required: true,
        }
    },
    computed: {
    },
    watch: {
    },
    async created() {
        this.isReady = false;
        const projects = await this.onGetProjects({projectUuid: this.projectUuid}); 
        this.project = projects[0];
        this.languages = await this.onGetLanguages();
        this.tags = await this.onGetTags();

        let data = await this.onGetProjectLanguages()
        this.projectLanguages = data.items

        this.isReady = true;
    },
    data() {
        return {
            searchQuery: "",
            project: null,
            resources: [],
            isReady: false,
            languages: [],
            projectLanguages: [],
            tags: [],
        }
    },
    methods: {
        searchResource() {
            this.$refs.resourceList.reloadResources();
        },
        getResources(params) {
            if (this.searchQuery.trim() !== "") {
                params.query = this.searchQuery.trim();
            }
            return this.onGetResources(params);
        },
        showAddResourceDlg() {
            this.$refs.addResource.show();
        },
        showEditResourceDlg(resource) {
            this.$refs.editResource.show(resource);
        },
        showDeleteResourceDlg(resource) {
            this.$refs.deleteResource.show(resource);
        },
        async addResource(params) {
            await this.onAddResource(params);
            this.tags = await this.onGetTags();
            this.$refs.resourceList.reloadCurrentPage();
        },
        async addGoogleDoc(params) {
            await this.onAddGoogleDoc(params);
            this.tags = await this.onGetTags();
            this.$refs.resourceList.reloadCurrentPage();
        },
        async editResource(params) {
            await this.onEditResource(params);
            this.tags = await this.onGetTags();
            this.$refs.resourceList.reloadCurrentPage();
        },
        async updateGoogleDoc(resource) {
            this.$refs.resourceList.showLoading();
            await this.addGoogleDoc({
                langCode: resource.primaryLanguage,
                url: resource.url,
                description: resource.description,
                tags: resource.tags,
            })
            this.$refs.resourceList.reloadCurrentPage();
        },
        async translationGoogle(resource) {
            this.$refs.resourceList.showLoading()
            await this.onTranslationGoogle(this.project, resource)
            this.$refs.resourceList.reloadCurrentPage()
        },
        async deleteResource(resource) {
            await this.onDeleteResource(resource);
            this.tags = await this.onGetTags();
            this.$refs.resourceList.reloadCurrentPage();
        },
        getDefaultLangCode() {
            const code = this.$refs.resourceList.getLanguageCodeFilter();
            if (code !== '' && code !== 'all') {
                return code;
            } else {
                return '';
            }
        }
    },
}
</script>

<style lang="scss" scoped>
@import 'theme';
</style>
